import { paths } from '@ui/config/paths';
import type { UseUserAgentsOptions } from '@ui/hooks/api/useUserAgents';
import { useRealPathname } from '@ui/hooks/useRealPathname';
import { routerWithI18n } from '@ui/locales/browserSSG';
import { pushRouterWithoutRerender } from '@ui/utils/pushRouterWithoutRerender';
import { useMemo } from 'react';
import { useLocation } from 'react-use';
import { create } from 'zustand';

interface ProfileDrawerStore {
  isOpen: boolean;
  account: string | null;
  tokenListType: UseUserAgentsOptions['type'];
  _drawerBodyElement: HTMLDivElement | null;
  open: (options: { account: string | null | undefined }) => void;
  close: () => void;
  drawerScrollTop: () => void;
}

const useProfileDrawerZustand = create<ProfileDrawerStore>(() => ({
  isOpen: false,
  account: null,
  tokenListType: 'holding',
  _drawerBodyElement: null,
  close: () => {
    const previousPath = routerWithI18n.asPath;
    if (!previousPath || isProfilePage(previousPath)) {
      pushRouterWithoutRerender(paths.home);
    } else {
      pushRouterWithoutRerender(previousPath);
    }
  },
  open: ({ account }) => {
    if (!account) return;
    pushRouterWithoutRerender(paths.profile(account));
  },
  drawerScrollTop: () => {
    useProfileDrawerZustand.getState()._drawerBodyElement?.scrollTo({
      top: 0,
    });
  },
}));

export const useProfileDrawer = <U>(
  selector: (state: ProfileDrawerStore) => U,
): U => {
  const realPathname = useRealPathname();
  const locationState = useLocation();

  new URLSearchParams(locationState.search).get('id');

  const isProfileDrawerOpen = useMemo(() => {
    return isProfilePage(realPathname);
  }, [realPathname]);

  return useProfileDrawerZustand((store) => {
    return selector({
      ...store,
      account:
        isProfileDrawerOpen && locationState.search
          ? new URLSearchParams(locationState.search).get('id')
          : null,
      isOpen: isProfileDrawerOpen,
    });
  });
};

useProfileDrawer.getState = useProfileDrawerZustand.getState;
useProfileDrawer.setState = useProfileDrawerZustand.setState;
useProfileDrawer.getInitialState = useProfileDrawerZustand.getInitialState;

const isProfilePage = (pathname: string) => {
  return !!pathname.match(/^\/profile/);
};
